import React, { useEffect, useMemo, useState } from "react";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

function formatDate(value) {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sv-SE");
}

function isAcceptedOverrideDecision(decision) {
  return ["accept", "unique_trigger"].includes(String(decision || "").trim());
}

function isRejectedOverrideDecision(decision) {
  return ["reject", "duplicate_trigger"].includes(String(decision || "").trim());
}

const OVERRIDE_CATEGORY_OPTIONS = [
  { value: "none", label: "Ingen override" },
  { value: "accepted", label: "Accepterad" },
  { value: "rejected", label: "Förkastad" },
];

function getOverrideCategory(row) {
  if (!row || !row.override_decision) return "none";
  if (isAcceptedOverrideDecision(row.override_decision)) return "accepted";
  if (isRejectedOverrideDecision(row.override_decision)) return "rejected";
  return "none";
}

function getOverrideDecisionLabel(decision) {
  const normalized = String(decision || "").trim();
  if (normalized === "accept") return "Accepterad";
  if (normalized === "unique_trigger") return "Unik";
  if (normalized === "duplicate_trigger") return "Dublett";
  if (normalized === "reject") return "Förkastad";
  return "–";
}

function extractEquivalentTriggerFingerprint(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;
  const patterns = [
    /Primary old=([a-f0-9]{64})/i,
    /\bvs ([a-f0-9]{64})\b/i,
    /Duplicate of ([a-f0-9]{64})/i,
  ];
  for (const p of patterns) {
    const m = raw.match(p);
    if (m?.[1]) return m[1].toLowerCase();
  }
  return null;
}

const EMPTY_MANUAL_TRIGGER = {
  title: "",
  trigger_category: "",
  jurisdiction: "",
  why_now: "",
  causal_chain: "",
  primary_source: "",
  secondary_sources: "",
  affected_sectors: "",
  buyers: "",
  what_is_bought: "",
  mode: "normal_pipeline",
};

const EMPTY_REVISION_FORM = {
  title: "",
  trigger_category: "",
  jurisdiction: "",
  why_now: "",
  causal_chain: "",
  primary_sources: "",
  secondary_sources: "",
  buyers: "",
  what_is_bought: "",
  affected_sectors: "",
  revision_reason: "",
  lock_manual_fields: false,
};

function toMultiline(value) {
  if (Array.isArray(value)) return value.join("\n");
  if (value == null) return "";
  return String(value);
}

function fromMultiline(value) {
  return String(value || "")
    .split(/\n|,/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function CheckboxDropdown({ label, summary, options, isChecked, onToggle, onSetAll, emptyText }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          padding: "10px 12px",
          borderRadius: "12px",
          border: "1px solid rgba(17,17,17,0.18)",
          background: "#fff",
          cursor: "pointer",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: "14px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {summary ? ` (${summary})` : ""} ▾
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40 }}
          />
          <div
            style={{
              position: "absolute",
              zIndex: 41,
              top: "calc(100% + 6px)",
              left: 0,
              minWidth: "240px",
              maxHeight: "320px",
              overflow: "auto",
              background: "#fff",
              border: "1px solid rgba(17,17,17,0.18)",
              borderRadius: "12px",
              boxShadow: "0 16px 48px rgba(17,17,17,0.18)",
              padding: "8px",
            }}
          >
            {onSetAll && options.length > 0 && (
              <div style={{ display: "flex", gap: "12px", padding: "4px 6px 8px", borderBottom: "1px solid rgba(17,17,17,0.08)", marginBottom: "4px" }}>
                <button type="button" onClick={() => onSetAll(true)} style={{ border: "none", background: "transparent", padding: 0, color: "#0b62a3", cursor: "pointer", fontSize: "13px" }}>Markera alla</button>
                <button type="button" onClick={() => onSetAll(false)} style={{ border: "none", background: "transparent", padding: 0, color: "#0b62a3", cursor: "pointer", fontSize: "13px" }}>Avmarkera alla</button>
              </div>
            )}
            {options.length === 0 ? (
              <div style={{ padding: "6px", color: "#7a746c", fontSize: "13px" }}>{emptyText || "Inga värden"}</div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt.value}
                  style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px", cursor: "pointer", fontSize: "14px" }}
                >
                  <input type="checkbox" checked={isChecked(opt.value)} onChange={() => onToggle(opt.value)} />
                  <span>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function TriggerOutcomesPage({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [excludeQuery, setExcludeQuery] = useState("");
  const [wentForwardFilter, setWentForwardFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  // "Dold"-modell: ett värde i mängden = dolt (kryssrutan avbockad).
  // Workflow döljer "rejected" som standard; override visar allt som standard.
  const [hiddenWorkflowStatuses, setHiddenWorkflowStatuses] = useState(() => new Set(["rejected"]));
  const [hiddenOverrideCategories, setHiddenOverrideCategories] = useState(() => new Set());
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [busyFp, setBusyFp] = useState(null);
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [showPipelineLog, setShowPipelineLog] = useState(true);
  const [pipelineConfirmOpen, setPipelineConfirmOpen] = useState(false);
  const [selectedFingerprint, setSelectedFingerprint] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [processHistory, setProcessHistory] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [comparePair, setComparePair] = useState({ left: null, right: null });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL_TRIGGER);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionBusy, setRevisionBusy] = useState(false);
  const [revisionForm, setRevisionForm] = useState(EMPTY_REVISION_FORM);

  const load = () => {
    setLoading(true);
    setError(null);
    apiFetch(`${getApiBase()}/triggers/outcomes`)
      .then((r) => parseJsonOrExplain(r))
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Kunde inte hämta triggerutfall"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    let timer = null;
    const fetchStatus = () => {
      apiFetch(`${getApiBase()}/admin/pipeline/status`)
        .then((r) => parseJsonOrExplain(r))
        .then((data) => setPipelineStatus(data || null))
        .catch(() => null);
    };
    fetchStatus();
    timer = setInterval(fetchStatus, 4000);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [user]);

  const reasonOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const reason = String(r.ai_reasoning || r.not_forward_reason || "").trim();
      if (reason) set.add(reason);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sv"));
  }, [rows]);

  const workflowOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const status = String(r.latest_status || "").trim();
      if (status) set.add(status);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sv"));
  }, [rows]);

  const workflowStatusOptions = useMemo(
    () => workflowOptions.map((s) => ({ value: s, label: s })),
    [workflowOptions]
  );

  const triggerTitleByFingerprint = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const fp = String(r.fingerprint || "").trim();
      if (!fp) return;
      map.set(fp, String(r.title || "").trim());
    });
    return map;
  }, [rows]);

  const rowByFingerprint = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const fp = String(r.fingerprint || "").trim();
      if (!fp) return;
      map.set(fp, r);
    });
    return map;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const ex = excludeQuery.trim().toLowerCase();
    const base = rows.filter((r) => {
      if (wentForwardFilter === "yes" && !r.went_forward) return false;
      if (wentForwardFilter === "no" && r.went_forward) return false;

      if (reasonFilter !== "all") {
        const rr = String(r.ai_reasoning || r.not_forward_reason || "");
        if (rr !== reasonFilter) return false;
      }

      if (hiddenOverrideCategories.has(getOverrideCategory(r))) return false;
      if (hiddenWorkflowStatuses.has(String(r.latest_status || ""))) return false;

      const title = String(r.title || "").toLowerCase();
      const fp = String(r.fingerprint || "").toLowerCase();
      const reason = String(r.ai_reasoning || r.not_forward_reason || "").toLowerCase();
      const text = `${title} ${fp} ${reason}`;
      if (q && !text.includes(q)) return false;
      if (ex && text.includes(ex)) return false;
      return true;
    });

    const dirMul = sortDir === "asc" ? 1 : -1;
    return base.slice().sort((a, b) => {
      let av;
      let bv;
      if (sortKey === "created_at") {
        av = new Date(a.created_at || 0).getTime();
        bv = new Date(b.created_at || 0).getTime();
      } else if (sortKey === "title") {
        av = String(a.title || "");
        bv = String(b.title || "");
      } else if (sortKey === "went_forward") {
        av = a.went_forward ? 1 : 0;
        bv = b.went_forward ? 1 : 0;
      } else if (sortKey === "not_forward_reason") {
        av = String(a.not_forward_reason || "");
        bv = String(b.not_forward_reason || "");
      } else if (sortKey === "override_decision") {
        av = String(a.override_decision || "");
        bv = String(b.override_decision || "");
      } else if (sortKey === "latest_status") {
        av = String(a.latest_status || "");
        bv = String(b.latest_status || "");
      } else {
        av = String(a[sortKey] || "");
        bv = String(b[sortKey] || "");
      }
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dirMul;
      return String(av).localeCompare(String(bv), "sv") * dirMul;
    });
  }, [rows, query, excludeQuery, wentForwardFilter, reasonFilter, hiddenOverrideCategories, hiddenWorkflowStatuses, sortKey, sortDir]);

  const hiddenCount = rows.length - filtered.length;
  const workflowCheckedCount = workflowStatusOptions.filter((o) => !hiddenWorkflowStatuses.has(o.value)).length;
  const overrideCheckedCount = OVERRIDE_CATEGORY_OPTIONS.filter((o) => !hiddenOverrideCategories.has(o.value)).length;

  const toggleHiddenValue = (setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const setAllHidden = (setter, optionValues, checked) => {
    setter((prev) => {
      const next = new Set(prev);
      optionValues.forEach((v) => {
        if (checked) next.delete(v);
        else next.add(v);
      });
      return next;
    });
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "created_at" ? "desc" : "asc");
  };

  const setDecision = (row, decision) => {
    const reasonPrompt =
      decision === "accept"
        ? "Valfri kommentar (varför ska triggern accepteras)?"
        : "Ange orsak till förkastning:";
    const reason = window.prompt(reasonPrompt, row.override_reason || "") || "";
    setBusyFp(row.fingerprint);
    apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(row.fingerprint)}/manual_decision`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        decision,
        reason,
        user: user?.email || "unknown",
      }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => load())
      .catch((e) => setError(e.message || "Kunde inte spara manuellt beslut"))
      .finally(() => setBusyFp(null));
  };

  const setNoveltyDecision = (row, decision) => {
    const promptText =
      decision === "unique_trigger"
        ? "Valfri kommentar (varför är detta en unik trigger)?"
        : "Valfri kommentar (varför är detta en dublett)?";
    const reason = window.prompt(promptText, "") || "";
    setBusyFp(row.fingerprint);
    apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(row.fingerprint)}/manual_decision`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        decision,
        reason,
        user: user?.email || "unknown",
      }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        load();
        setCompareOpen(false);
      })
      .catch((e) => setError(e.message || "Kunde inte spara novelty-beslut"))
      .finally(() => setBusyFp(null));
  };

  const submitManualTrigger = () => {
    const payload = {
      title: manualForm.title,
      trigger_category: manualForm.trigger_category,
      jurisdiction: manualForm.jurisdiction,
      why_now: manualForm.why_now,
      causal_chain: manualForm.causal_chain,
      primary_sources: [manualForm.primary_source],
      secondary_sources: manualForm.secondary_sources,
      affected_sectors: manualForm.affected_sectors,
      buyers: manualForm.buyers,
      what_is_bought: manualForm.what_is_bought,
      mode: manualForm.mode,
    };

    setManualBusy(true);
    setError(null);
    apiFetch(`${getApiBase()}/triggers/manual`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setManualOpen(false);
        setManualForm(EMPTY_MANUAL_TRIGGER);
        load();
      })
      .catch((e) => setError(e.message || "Kunde inte skapa manuell trigger"))
      .finally(() => setManualBusy(false));
  };

  const openRevisionModal = () => {
    if (!detail?.raw_json) return;
    const raw = detail.raw_json || {};
    setRevisionForm({
      title: String(raw.title || ""),
      trigger_category: String(raw.trigger_category || ""),
      jurisdiction: String(raw.jurisdiction || ""),
      why_now: String(raw.why_now || ""),
      causal_chain: String(raw.causal_chain || ""),
      primary_sources: toMultiline(raw.primary_sources),
      secondary_sources: toMultiline(raw.secondary_sources),
      buyers: toMultiline(raw.buyers),
      what_is_bought: toMultiline(raw.what_is_bought),
      affected_sectors: toMultiline(raw.affected_sectors),
      revision_reason: "",
      lock_manual_fields: false,
    });
    setRevisionOpen(true);
  };

  const submitRevision = () => {
    if (!detail?.fingerprint) return;
    setRevisionBusy(true);
    setError(null);
    apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(detail.fingerprint)}/revise`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: revisionForm.title,
        trigger_category: revisionForm.trigger_category,
        jurisdiction: revisionForm.jurisdiction,
        why_now: revisionForm.why_now,
        causal_chain: revisionForm.causal_chain,
        primary_sources: fromMultiline(revisionForm.primary_sources),
        secondary_sources: fromMultiline(revisionForm.secondary_sources),
        buyers: fromMultiline(revisionForm.buyers),
        what_is_bought: fromMultiline(revisionForm.what_is_bought),
        affected_sectors: fromMultiline(revisionForm.affected_sectors),
        revision_reason: revisionForm.revision_reason,
        lock_manual_fields: revisionForm.lock_manual_fields,
        run_mode: "reprocess",
      }),
    })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => {
        setRevisionOpen(false);
        setRevisionForm(EMPTY_REVISION_FORM);
        load();
        if (data?.new_fingerprint) {
          openDetails(data.new_fingerprint);
        }
      })
      .catch((e) => setError(e.message || "Kunde inte skapa triggerrevision"))
      .finally(() => setRevisionBusy(false));
  };

  const startPipelineNow = () => {
    if (user?.role !== "admin") return;
    setPipelineBusy(true);
    apiFetch(`${getApiBase()}/admin/pipeline/run`, { method: "POST" })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setError(null);
        return apiFetch(`${getApiBase()}/admin/pipeline/status`);
      })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => setPipelineStatus(data || null))
      .catch((e) => setError(e.message || "Kunde inte starta pipeline"))
      .finally(() => setPipelineBusy(false));
  };

  const runPipelineNow = () => {
    if (user?.role !== "admin") return;
    setPipelineConfirmOpen(true);
  };

  const runAnalystQueueOnlyNow = () => {
    if (user?.role !== "admin") return;
    setPipelineBusy(true);
    apiFetch(`${getApiBase()}/admin/pipeline/run-analyst-queue`, { method: "POST" })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setError(null);
        return apiFetch(`${getApiBase()}/admin/pipeline/status`);
      })
      .then((r) => parseJsonOrExplain(r))
      .then((data) => setPipelineStatus(data || null))
      .catch((e) => setError(e.message || "Kunde inte starta analyst_queue-körning"))
      .finally(() => setPipelineBusy(false));
  };

  const getDisplayWorkflowStatus = (row) => {
    const status = String(row.latest_status || "").trim();
    if (status) return status;
    if (row.override_decision === "accept") return "new (manual override)";
    if (row.override_decision === "reject") return "rejected (manual override)";
    return "";
  };

  const getDisplayNotForwardReason = (row) => {
    const status = String(row.latest_status || "").trim();
    if (status !== "rejected") return "–";
    return row.ai_reasoning || row.not_forward_reason || "–";
  };

  const getWentForwardDisplay = (row) => {
    const status = String(row.latest_status || "").trim();
    if (status === "needs_novelty_review") {
      return { label: "Kräver bedömning", color: "#b54708" };
    }
    return row.went_forward
      ? { label: "Ja", color: "#0a7f5a" }
      : { label: "Nej", color: "#b42318" };
  };

  const openDetails = (fingerprint) => {
    setSelectedFingerprint(fingerprint);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    setProcessHistory([]);
    Promise.all([
      apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(fingerprint)}`).then((r) => parseJsonOrExplain(r)),
      apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(fingerprint)}/process_history`).then((r) => parseJsonOrExplain(r)),
    ])
      .then(([triggerData, historyData]) => {
        setDetail(triggerData || null);
        setProcessHistory(Array.isArray(historyData) ? historyData : []);
      })
      .catch((e) => setDetailError(e.message || "Kunde inte hämta triggerdetaljer"))
      .finally(() => setDetailLoading(false));
  };

  const openComparison = (leftFingerprint, rightFingerprint) => {
    if (!leftFingerprint || !rightFingerprint || leftFingerprint === rightFingerprint) return;
    setCompareOpen(true);
    setCompareLoading(true);
    setCompareError(null);
    setComparePair({ left: null, right: null });
    Promise.all([
      apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(leftFingerprint)}`).then((r) => parseJsonOrExplain(r)),
      apiFetch(`${getApiBase()}/triggers/${encodeURIComponent(rightFingerprint)}`).then((r) => parseJsonOrExplain(r)),
    ])
      .then(([leftTrigger, rightTrigger]) => {
        setComparePair({
          left: leftTrigger || null,
          right: rightTrigger || null,
        });
      })
      .catch((e) => setCompareError(e.message || "Kunde inte hämta triggerjämförelse"))
      .finally(() => setCompareLoading(false));
  };

  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginBottom: "14px", gap: "12px", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "30px", fontWeight: 500 }}>Triggerutfall</h2>
          <p style={{ margin: "8px 0 0 0", color: "#5f5a52", fontFamily: "Arial, Helvetica, sans-serif" }}>
            Visar om triggers gått vidare, varför de stoppats och låter dig manuellt överstyra beslut.
          </p>
        </div>
        {user?.role === "admin" && (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setManualOpen(true)}
              style={{
                padding: "10px 16px",
                borderRadius: "999px",
                border: "1px solid rgba(17,17,17,0.2)",
                background: "#fff",
                color: "#111",
                cursor: "pointer",
                fontFamily: "Arial, Helvetica, sans-serif",
              }}
            >
              Ny trigger
            </button>
            <button
              type="button"
              onClick={runPipelineNow}
              disabled={pipelineBusy || pipelineStatus?.running}
              style={{
                padding: "10px 16px",
                borderRadius: "999px",
                border: "1px solid #111",
                background: "#111",
                color: "#fff",
                cursor: pipelineBusy || pipelineStatus?.running ? "wait" : "pointer",
                fontFamily: "Arial, Helvetica, sans-serif",
              }}
            >
              {pipelineStatus?.running ? "Pipeline kör..." : pipelineBusy ? "Startar..." : "Kör pipeline manuellt"}
            </button>
            <button
              type="button"
              onClick={runAnalystQueueOnlyNow}
              disabled={pipelineBusy || pipelineStatus?.running}
              style={{
                padding: "10px 16px",
                borderRadius: "999px",
                border: "1px solid rgba(17,17,17,0.2)",
                background: "#fff",
                color: "#111",
                cursor: pipelineBusy || pipelineStatus?.running ? "wait" : "pointer",
                fontFamily: "Arial, Helvetica, sans-serif",
              }}
            >
              {pipelineBusy ? "Startar..." : "Kör endast analyst_queue"}
            </button>
          </div>
        )}
      </div>

      {user?.role === "admin" && pipelineStatus && (
        <div style={{ marginBottom: "14px", padding: "10px 12px", border: "1px solid rgba(17,17,17,0.12)", borderRadius: "12px", fontFamily: "Arial, Helvetica, sans-serif", background: "#fff" }}>
          <div style={{ fontSize: "13px", color: "#3f3a34", marginBottom: "8px" }}>
            Status: {pipelineStatus.running ? "Körs" : "Idle"}
            {pipelineStatus.started_at ? ` · Start: ${formatDate(pipelineStatus.started_at)}` : ""}
            {pipelineStatus.finished_at ? ` · Klar: ${formatDate(pipelineStatus.finished_at)}` : ""}
            {pipelineStatus.exit_code !== null && pipelineStatus.exit_code !== undefined ? ` · Exit: ${pipelineStatus.exit_code}` : ""}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
            <button
              type="button"
              onClick={() => setShowPipelineLog((v) => !v)}
              style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "4px 10px", cursor: "pointer", fontSize: "12px" }}
            >
              {showPipelineLog ? "Dölj logg" : "Visa logg"}
            </button>
            <span style={{ fontSize: "12px", color: "#6f665d" }}>
              Loggen uppdateras automatiskt medan pipelinen kör.
            </span>
          </div>
          {showPipelineLog && (
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                fontSize: "12px",
                lineHeight: 1.35,
                color: "#1f1b16",
                background: "#fcfbf8",
                border: "1px solid rgba(17,17,17,0.10)",
                borderRadius: "10px",
                padding: "10px",
                maxHeight: "280px",
                overflow: "auto",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              }}
            >
              {[
                pipelineStatus.stdout_tail ? pipelineStatus.stdout_tail : "",
                pipelineStatus.stderr_tail ? `\n[stderr]\n${pipelineStatus.stderr_tail}` : "",
              ]
                .join("")
                .trim() || "Ingen logg tillgänglig ännu."}
            </pre>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "14px", fontFamily: "Arial, Helvetica, sans-serif" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Sök text (inkludera)"
          style={{ minWidth: "220px", flex: "1 1 260px", padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(17,17,17,0.18)" }}
        />
        <input
          value={excludeQuery}
          onChange={(e) => setExcludeQuery(e.target.value)}
          placeholder="Sök text som INTE ska ingå"
          style={{ minWidth: "220px", flex: "1 1 260px", padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(17,17,17,0.18)" }}
        />
        <select value={wentForwardFilter} onChange={(e) => setWentForwardFilter(e.target.value)} style={{ padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(17,17,17,0.18)" }}>
          <option value="all">Gått vidare: Alla</option>
          <option value="yes">Gått vidare: Ja</option>
          <option value="no">Gått vidare: Nej</option>
        </select>
        <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} style={{ padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(17,17,17,0.18)", minWidth: "220px" }}>
          <option value="all">Varför ej vidare: Alla</option>
          {reasonOptions.map((reason) => (
            <option key={reason} value={reason}>{reason}</option>
          ))}
        </select>
        <CheckboxDropdown
          label="Manuell override"
          summary={`${overrideCheckedCount}/${OVERRIDE_CATEGORY_OPTIONS.length}`}
          options={OVERRIDE_CATEGORY_OPTIONS}
          isChecked={(value) => !hiddenOverrideCategories.has(value)}
          onToggle={(value) => toggleHiddenValue(setHiddenOverrideCategories, value)}
          onSetAll={(checked) => setAllHidden(setHiddenOverrideCategories, OVERRIDE_CATEGORY_OPTIONS.map((o) => o.value), checked)}
        />
        <CheckboxDropdown
          label="Workflow-status"
          summary={`${workflowCheckedCount}/${workflowStatusOptions.length}`}
          options={workflowStatusOptions}
          isChecked={(value) => !hiddenWorkflowStatuses.has(value)}
          onToggle={(value) => toggleHiddenValue(setHiddenWorkflowStatuses, value)}
          onSetAll={(checked) => setAllHidden(setHiddenWorkflowStatuses, workflowStatusOptions.map((o) => o.value), checked)}
          emptyText="Inga statusvärden"
        />
        <button
          type="button"
          onClick={load}
          style={{ padding: "10px 14px", borderRadius: "999px", border: "1px solid rgba(17,17,17,0.2)", background: "#fff", cursor: "pointer" }}
        >
          Uppdatera
        </button>
      </div>

      <div style={{ marginBottom: "12px", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "13px", color: "#5f5a52" }}>
        Visar {filtered.length} av {rows.length} triggers
        {hiddenCount > 0 && (
          <span> · <strong>{hiddenCount}</strong> utfiltrerade</span>
        )}
      </div>

      {error && <p style={{ color: "#b42318", fontFamily: "Arial, Helvetica, sans-serif" }}>{error}</p>}

      <div style={{ overflowX: "auto", border: "1px solid rgba(17,17,17,0.10)", borderRadius: "14px", background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "14px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(17,17,17,0.12)" }}>
              <th onClick={() => toggleSort("title")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Trigger</th>
              <th onClick={() => toggleSort("created_at")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Skapad</th>
              <th onClick={() => toggleSort("went_forward")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Gått vidare</th>
              <th onClick={() => toggleSort("latest_status")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Workflow-status</th>
              <th onClick={() => toggleSort("not_forward_reason")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Varför ej vidare</th>
              <th onClick={() => toggleSort("override_decision")} style={{ textAlign: "left", padding: "12px 14px", cursor: "pointer" }}>Manuell override</th>
              <th style={{ textAlign: "left", padding: "12px 14px" }}>Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={{ padding: "18px 14px", color: "#5f5a52" }} colSpan={7}>Laddar…</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td style={{ padding: "18px 14px", color: "#5f5a52" }} colSpan={7}>Inga träffar.</td>
              </tr>
            ) : (
              filtered.map((r) => {
                const busy = busyFp === r.fingerprint;
                const isSelected = selectedFingerprint === r.fingerprint;
                const forwardDisplay = getWentForwardDisplay(r);
                const equivalentFp = extractEquivalentTriggerFingerprint(
                  r.ai_reasoning || r.not_forward_reason || ""
                );
                const hasEquivalent =
                  Boolean(equivalentFp) && equivalentFp !== r.fingerprint;
                const equivalentTitle =
                  (hasEquivalent && triggerTitleByFingerprint.get(equivalentFp)) || equivalentFp;
                const isNeedsNoveltyReview = String(r.latest_status || "") === "needs_novelty_review";
                return (
                  <React.Fragment key={r.fingerprint}>
                  <tr
                    onClick={() => openDetails(r.fingerprint)}
                    style={{
                      borderBottom: isSelected ? "none" : "1px solid rgba(17,17,17,0.06)",
                      cursor: "pointer",
                      background: isSelected ? "rgba(11,98,163,0.06)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "12px 14px", maxWidth: "360px" }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetails(r.fingerprint);
                        }}
                        style={{
                          fontWeight: 500,
                          lineHeight: 1.35,
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          margin: 0,
                          textAlign: "left",
                          cursor: "pointer",
                          color: "#0b62a3",
                          textDecoration: "underline",
                        }}
                      >
                        {r.title || r.fingerprint}
                      </button>
                      <div style={{ fontSize: "12px", color: "#7a746c", marginTop: "3px" }}>{r.fingerprint}</div>
                      <div style={{ fontSize: "12px", color: "#7a746c", marginTop: "3px" }}>
                        Bolag: {r.company_count} · Intressanta: {r.interesting_count} · Ej: {r.not_interesting_count}
                      </div>
                      {hasEquivalent && (
                        <div style={{ fontSize: "12px", color: "#7a746c", marginTop: "3px" }}>
                          Likvärdig med:{" "}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openComparison(r.fingerprint, equivalentFp);
                            }}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              margin: 0,
                              color: "#0b62a3",
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            {equivalentTitle}
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>{formatDate(r.created_at)}</td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap", fontWeight: 600, color: forwardDisplay.color }}>
                      {forwardDisplay.label}
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "12px" }}>
                      {getDisplayWorkflowStatus(r) || "–"}
                    </td>
                    <td style={{ padding: "12px 14px", maxWidth: "260px", color: "#333" }}>
                      {getDisplayNotForwardReason(r)}
                    </td>
                    <td style={{ padding: "12px 14px", maxWidth: "220px" }}>
                      {r.override_decision ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{getOverrideDecisionLabel(r.override_decision)}</div>
                          {r.override_reason && <div style={{ fontSize: "12px", color: "#5f5a52" }}>{r.override_reason}</div>}
                          {r.override_user && <div style={{ fontSize: "11px", color: "#9a9288" }}>{r.override_user}</div>}
                        </div>
                      ) : "–"}
                    </td>
                    <td style={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {isNeedsNoveltyReview ? (
                          <>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                setNoveltyDecision(r, "unique_trigger");
                              }}
                              style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #0a7f5a", background: "#fff", color: "#0a7f5a", cursor: busy ? "wait" : "pointer" }}
                            >
                              Unik trigger
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                setNoveltyDecision(r, "duplicate_trigger");
                              }}
                              style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #b42318", background: "#fff", color: "#b42318", cursor: busy ? "wait" : "pointer" }}
                            >
                              Dublett
                            </button>
                          </>
                        ) : (
                          ["rejected", "duplicate_trigger"].includes(String(r.latest_status || "")) && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDecision(r, "accept");
                            }}
                            style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #0a7f5a", background: "#fff", color: "#0a7f5a", cursor: busy ? "wait" : "pointer" }}
                          >
                            Acceptera
                          </button>
                          )
                        )}
                        {!isNeedsNoveltyReview && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDecision(r, "reject");
                            }}
                            style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #b42318", background: "#fff", color: "#b42318", cursor: busy ? "wait" : "pointer" }}
                          >
                            Förkasta
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isSelected && (
                    <tr style={{ background: "#f8fbff", borderBottom: "1px solid rgba(17,17,17,0.06)" }}>
                      <td colSpan={7} style={{ padding: "14px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 500 }}>Triggerdetaljer</h3>
                          <div style={{ display: "flex", gap: "8px" }}>
                            {user?.role === "admin" && detail?.raw_json && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRevisionModal();
                                }}
                                style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: "pointer" }}
                              >
                                Revidera trigger
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFingerprint(null);
                                setDetail(null);
                                setDetailError(null);
                                setProcessHistory([]);
                              }}
                              style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: "pointer" }}
                            >
                              Stäng
                            </button>
                          </div>
                        </div>
                        {detailLoading ? (
                          <p style={{ margin: 0, color: "#5f5a52" }}>Laddar triggerdetaljer...</p>
                        ) : detailError ? (
                          <p style={{ margin: 0, color: "#b42318" }}>{detailError}</p>
                        ) : detail?.raw_json ? (
                          <div style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "14px", color: "#222" }}>
                            <div style={{ marginBottom: "8px" }}><strong>Titel:</strong> {detail.raw_json.title || "–"}</div>
                            <div style={{ marginBottom: "8px" }}><strong>Kategori:</strong> {detail.raw_json.trigger_category || "–"}</div>
                            <div style={{ marginBottom: "8px" }}><strong>Jurisdiktion:</strong> {detail.raw_json.jurisdiction || "–"}</div>
                            <div style={{ marginBottom: "8px" }}><strong>Why now:</strong> {detail.raw_json.why_now || "–"}</div>
                            <div style={{ marginBottom: "8px" }}><strong>Causal chain:</strong> {detail.raw_json.causal_chain || "–"}</div>
                            <div style={{ marginBottom: "8px" }}>
                              <strong>Affected sectors:</strong> {Array.isArray(detail.raw_json.affected_sectors) ? detail.raw_json.affected_sectors.join(", ") : "–"}
                            </div>
                            <div style={{ marginBottom: "8px" }}>
                              <strong>Relevanta lankar:</strong>
                              <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                                {[
                                  ...(Array.isArray(detail.raw_json.primary_sources) ? detail.raw_json.primary_sources : []),
                                  ...(Array.isArray(detail.raw_json.secondary_sources) ? detail.raw_json.secondary_sources : []),
                                ].map((url) => (
                                  <li key={url}>
                                    <a href={url} target="_blank" rel="noreferrer">{url}</a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div style={{ marginTop: "12px" }}>
                              <strong>Processhistorik:</strong>
                              {processHistory.length === 0 ? (
                                <div style={{ marginTop: "6px", color: "#5f5a52" }}>Ingen historik hittades.</div>
                              ) : (
                                <div style={{ marginTop: "8px", overflowX: "auto" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                      <tr>
                                        <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.12)" }}>Tid</th>
                                        <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.12)" }}>Status</th>
                                        <th style={{ textAlign: "left", padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.12)" }}>Notering</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {processHistory.map((h, idx) => (
                                        <tr key={`${h.created_at || "t"}-${h.status || "s"}-${idx}`}>
                                          <td style={{ padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.06)" }}>{formatDate(h.created_at)}</td>
                                          <td style={{ padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.06)", fontFamily: "monospace" }}>{h.status || "–"}</td>
                                          <td style={{ padding: "6px", borderBottom: "1px solid rgba(17,17,17,0.06)" }}>{h.reason || h.analyst_notes || h.scout_notes || "–"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p style={{ margin: 0, color: "#5f5a52" }}>Ingen detaljdata tillganglig.</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {manualOpen && (
        <div
          onClick={() => !manualBusy && setManualOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(900px, 98vw)",
              maxHeight: "90vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid rgba(17,17,17,0.14)",
              boxShadow: "0 24px 80px rgba(17,17,17,0.26)",
              padding: "16px",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Skapa manuell trigger</h3>
              <button
                type="button"
                disabled={manualBusy}
                onClick={() => setManualOpen(false)}
                style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: manualBusy ? "wait" : "pointer" }}
              >
                Stäng
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Titel*</span>
                <input value={manualForm.title} onChange={(e) => setManualForm((v) => ({ ...v, title: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Triggerkategori*</span>
                <input value={manualForm.trigger_category} onChange={(e) => setManualForm((v) => ({ ...v, trigger_category: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Jurisdiktion*</span>
                <input value={manualForm.jurisdiction} onChange={(e) => setManualForm((v) => ({ ...v, jurisdiction: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Primär källa (URL)*</span>
                <input value={manualForm.primary_source} onChange={(e) => setManualForm((v) => ({ ...v, primary_source: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
              <span>Why now*</span>
              <textarea rows={3} value={manualForm.why_now} onChange={(e) => setManualForm((v) => ({ ...v, why_now: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}>
              <span>Causal chain</span>
              <textarea rows={3} value={manualForm.causal_chain} onChange={(e) => setManualForm((v) => ({ ...v, causal_chain: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Affected sectors (komma-separerat)</span>
                <input value={manualForm.affected_sectors} onChange={(e) => setManualForm((v) => ({ ...v, affected_sectors: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>Buyers (komma-separerat)</span>
                <input value={manualForm.buyers} onChange={(e) => setManualForm((v) => ({ ...v, buyers: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span>What is bought (komma-separerat)</span>
                <input value={manualForm.what_is_bought} onChange={(e) => setManualForm((v) => ({ ...v, what_is_bought: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
              </label>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "12px" }}>
              <span>Sekundära källor (komma-separerade URL:er)</span>
              <input value={manualForm.secondary_sources} onChange={(e) => setManualForm((v) => ({ ...v, secondary_sources: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
              <span>Routing</span>
              <select value={manualForm.mode} onChange={(e) => setManualForm((v) => ({ ...v, mode: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }}>
                <option value="normal_pipeline">Normal pipeline (status: new)</option>
                <option value="fast_track">Snabbspår till analyst_queue</option>
              </select>
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button type="button" disabled={manualBusy} onClick={() => setManualOpen(false)} style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid rgba(17,17,17,0.2)", background: "#fff", cursor: manualBusy ? "wait" : "pointer" }}>Avbryt</button>
              <button type="button" disabled={manualBusy} onClick={submitManualTrigger} style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid #111", background: "#111", color: "#fff", cursor: manualBusy ? "wait" : "pointer" }}>{manualBusy ? "Skapar..." : "Skapa trigger"}</button>
            </div>
          </div>
        </div>
      )}

      {revisionOpen && (
        <div
          onClick={() => !revisionBusy && setRevisionOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(960px, 98vw)",
              maxHeight: "90vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid rgba(17,17,17,0.14)",
              boxShadow: "0 24px 80px rgba(17,17,17,0.26)",
              padding: "16px",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Revidera trigger</h3>
              <button type="button" disabled={revisionBusy} onClick={() => setRevisionOpen(false)} style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: revisionBusy ? "wait" : "pointer" }}>Stäng</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Titel*</span><input value={revisionForm.title} onChange={(e) => setRevisionForm((v) => ({ ...v, title: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Triggerkategori*</span><input value={revisionForm.trigger_category} onChange={(e) => setRevisionForm((v) => ({ ...v, trigger_category: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Jurisdiktion*</span><input value={revisionForm.jurisdiction} onChange={(e) => setRevisionForm((v) => ({ ...v, jurisdiction: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)" }} /></label>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}><span>Why now*</span><textarea rows={3} value={revisionForm.why_now} onChange={(e) => setRevisionForm((v) => ({ ...v, why_now: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}><span>Causal chain</span><textarea rows={3} value={revisionForm.causal_chain} onChange={(e) => setRevisionForm((v) => ({ ...v, causal_chain: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Buyers (en per rad)</span><textarea rows={4} value={revisionForm.buyers} onChange={(e) => setRevisionForm((v) => ({ ...v, buyers: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>What is bought (en per rad)</span><textarea rows={4} value={revisionForm.what_is_bought} onChange={(e) => setRevisionForm((v) => ({ ...v, what_is_bought: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Affected sectors (en per rad)</span><textarea rows={4} value={revisionForm.affected_sectors} onChange={(e) => setRevisionForm((v) => ({ ...v, affected_sectors: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Primära källor (URL, en per rad)*</span><textarea rows={5} value={revisionForm.primary_sources} onChange={(e) => setRevisionForm((v) => ({ ...v, primary_sources: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
              <label style={{ display: "flex", flexDirection: "column", gap: "6px" }}><span>Sekundära källor (URL, en per rad)</span><textarea rows={5} value={revisionForm.secondary_sources} onChange={(e) => setRevisionForm((v) => ({ ...v, secondary_sources: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "10px" }}><span>Ändringsmotivering*</span><textarea rows={3} value={revisionForm.revision_reason} onChange={(e) => setRevisionForm((v) => ({ ...v, revision_reason: e.target.value }))} style={{ padding: "10px 12px", borderRadius: "10px", border: "1px solid rgba(17,17,17,0.2)", resize: "vertical" }} /></label>
            <label style={{ display: "inline-flex", gap: "8px", alignItems: "center", marginBottom: "14px" }}>
              <input type="checkbox" checked={revisionForm.lock_manual_fields} onChange={(e) => setRevisionForm((v) => ({ ...v, lock_manual_fields: e.target.checked }))} />
              <span>Lås manuella fält (buyers / what_is_bought / affected_sectors) vid AI-omtolkning</span>
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button type="button" disabled={revisionBusy} onClick={() => setRevisionOpen(false)} style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid rgba(17,17,17,0.2)", background: "#fff", cursor: revisionBusy ? "wait" : "pointer" }}>Avbryt</button>
              <button type="button" disabled={revisionBusy} onClick={submitRevision} style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid #111", background: "#111", color: "#fff", cursor: revisionBusy ? "wait" : "pointer" }}>{revisionBusy ? "Sparar..." : "Spara revision"}</button>
            </div>
          </div>
        </div>
      )}

      {compareOpen && (
        <div
          onClick={() => setCompareOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1280px, 98vw)",
              maxHeight: "90vh",
              overflow: "auto",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid rgba(17,17,17,0.14)",
              boxShadow: "0 24px 80px rgba(17,17,17,0.26)",
              padding: "16px",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Jämför triggers</h3>
              <button
                type="button"
                onClick={() => setCompareOpen(false)}
                style={{ border: "1px solid rgba(17,17,17,0.2)", background: "#fff", borderRadius: "999px", padding: "6px 10px", cursor: "pointer" }}
              >
                Stäng
              </button>
            </div>

            {compareLoading ? (
              <p style={{ margin: 0, color: "#5f5a52" }}>Laddar jämförelse...</p>
            ) : compareError ? (
              <p style={{ margin: 0, color: "#b42318" }}>{compareError}</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {[{ key: "left", label: "Aktuell trigger" }, { key: "right", label: "Likvärdig trigger" }].map((col) => {
                  const data = comparePair[col.key];
                  const raw = data?.raw_json || {};
                  const workflowRow = data?.fingerprint ? rowByFingerprint.get(String(data.fingerprint)) : null;
                  const workflowStatus = String(workflowRow?.latest_status || "");
                  const needsNoveltyReview = workflowStatus === "needs_novelty_review";
                  const compareBusy = busyFp === data?.fingerprint;
                  const allLinks = [
                    ...(Array.isArray(raw.primary_sources) ? raw.primary_sources : []),
                    ...(Array.isArray(raw.secondary_sources) ? raw.secondary_sources : []),
                  ];
                  return (
                    <div
                      key={col.key}
                      style={{
                        border: "1px solid rgba(17,17,17,0.12)",
                        borderRadius: "12px",
                        padding: "12px",
                        background: "#fbfaf7",
                      }}
                    >
                      <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#5f5a52", marginBottom: "8px" }}>
                        {col.label}
                      </div>
                      {!data ? (
                        <div style={{ color: "#5f5a52", fontSize: "13px" }}>Ingen data tillgänglig.</div>
                      ) : (
                        <>
                          <div style={{ fontSize: "20px", lineHeight: 1.25, marginBottom: "6px" }}>
                            {raw.title || data.fingerprint}
                          </div>
                          <div style={{ fontSize: "12px", color: "#7a746c", marginBottom: "10px", wordBreak: "break-all" }}>
                            {data.fingerprint}
                          </div>
                          <div style={{ fontSize: "12px", color: "#7a746c", marginBottom: "10px" }}>
                            Workflow-status: {workflowStatus || "–"}
                          </div>
                          {needsNoveltyReview && (
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
                              <button
                                type="button"
                                disabled={compareBusy}
                                onClick={() => setNoveltyDecision(workflowRow, "unique_trigger")}
                                style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #0a7f5a", background: "#fff", color: "#0a7f5a", cursor: compareBusy ? "wait" : "pointer" }}
                              >
                                Unik trigger
                              </button>
                              <button
                                type="button"
                                disabled={compareBusy}
                                onClick={() => setNoveltyDecision(workflowRow, "duplicate_trigger")}
                                style={{ padding: "6px 10px", borderRadius: "999px", border: "1px solid #b42318", background: "#fff", color: "#b42318", cursor: compareBusy ? "wait" : "pointer" }}
                              >
                                Dublett
                              </button>
                            </div>
                          )}
                          <div style={{ fontSize: "13px", marginBottom: "6px" }}><strong>Kategori:</strong> {raw.trigger_category || "–"}</div>
                          <div style={{ fontSize: "13px", marginBottom: "6px" }}><strong>Jurisdiktion:</strong> {raw.jurisdiction || "–"}</div>
                          <div style={{ fontSize: "13px", marginBottom: "6px" }}><strong>Why now:</strong> {raw.why_now || "–"}</div>
                          <div style={{ fontSize: "13px", marginBottom: "6px" }}><strong>Causal chain:</strong> {raw.causal_chain || "–"}</div>
                          <div style={{ fontSize: "13px", marginBottom: "6px" }}>
                            <strong>Affected sectors:</strong> {Array.isArray(raw.affected_sectors) && raw.affected_sectors.length > 0 ? raw.affected_sectors.join(", ") : "–"}
                          </div>
                          <div style={{ fontSize: "13px", marginBottom: "4px" }}><strong>Källor:</strong></div>
                          {allLinks.length === 0 ? (
                            <div style={{ fontSize: "13px", color: "#5f5a52" }}>–</div>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", lineHeight: 1.45 }}>
                              {allLinks.map((url) => (
                                <li key={url}>
                                  <a href={url} target="_blank" rel="noreferrer">{url}</a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {pipelineConfirmOpen && (
        <div
          onClick={() => !pipelineBusy && setPipelineConfirmOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,17,17,0.45)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(520px, 96vw)",
              background: "#fff",
              borderRadius: "18px",
              border: "1px solid rgba(17,17,17,0.14)",
              boxShadow: "0 24px 80px rgba(17,17,17,0.26)",
              padding: "18px",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <h3 style={{ margin: "0 0 10px 0", fontSize: "22px", fontWeight: 600 }}>Alpha Hunter</h3>
            <p style={{ margin: "0 0 16px 0", color: "#3f3a34", fontSize: "16px" }}>Vill du köra en ny trigger sökning</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                disabled={pipelineBusy}
                onClick={() => setPipelineConfirmOpen(false)}
                style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid rgba(17,17,17,0.2)", background: "#fff", cursor: pipelineBusy ? "wait" : "pointer" }}
              >
                Avbryt
              </button>
              <button
                type="button"
                disabled={pipelineBusy}
                onClick={() => {
                  setPipelineConfirmOpen(false);
                  startPipelineNow();
                }}
                style={{ padding: "8px 12px", borderRadius: "999px", border: "1px solid #111", background: "#111", color: "#fff", cursor: pipelineBusy ? "wait" : "pointer" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}

